'use client';

import React from 'react';
import { Megaphone, DollarSign, TrendingUp, MousePointerClick } from 'lucide-react';
import { Campaign } from './CampaignManagementContent';
import Icon from '@/components/ui/AppIcon';


interface Props {
  campaigns: Campaign[];
}

export default function CampaignKPICards({ campaigns }: Props) {
  const active = campaigns.filter((c) => c.status === 'active');
  const totalSpend = active.reduce((s, c) => s + c.spend, 0);
  const avgRoas = active.length > 0 ? (active.reduce((s, c) => s + c.roas, 0) / active.length) : 0;
  const avgCtr = active.length > 0 ? (active.reduce((s, c) => s + c.ctr, 0) / active.length) : 0;

  const cards = [
    {
      id: 'kpi-camp-active',
      label: 'Active Campaigns',
      value: active.length.toString(),
      sub: `${campaigns.filter((c) => c.status === 'review').length} awaiting review`,
      icon: Megaphone,
      color: 'bg-primary/10 text-primary',
    },
    {
      id: 'kpi-camp-spend',
      label: 'Total Spend (Active)',
      value: `$${(totalSpend / 1000).toFixed(1)}k`,
      sub: `$${(active.reduce((s, c) => s + c.budget, 0) / 1000).toFixed(1)}k total budget`,
      icon: DollarSign,
      color: 'bg-accent/10 text-accent',
    },
    {
      id: 'kpi-camp-roas',
      label: 'Avg. ROAS',
      value: `${avgRoas.toFixed(2)}×`,
      sub: avgRoas >= 3.0 ? 'Above 3.0× target' : 'Below 3.0× target',
      icon: TrendingUp,
      color: avgRoas >= 3.0 ? 'bg-positive/10 text-positive' : 'bg-negative/10 text-negative',
    },
    {
      id: 'kpi-camp-ctr',
      label: 'Avg. CTR',
      value: `${avgCtr.toFixed(2)}%`,
      sub: avgCtr >= 0.8 ? 'Above 0.8% threshold' : 'Below 0.8% — review creatives',
      icon: MousePointerClick,
      color: avgCtr >= 0.8 ? 'bg-positive/10 text-positive' : 'bg-negative/10 text-negative',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <div key={card.id} className="card-base p-5 card-hover">
            <div className="flex items-start justify-between mb-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{card.label}</p>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${card.color}`}>
                <Icon size={15} />
              </div>
            </div>
            <p className="text-3xl font-bold text-foreground tabular-nums">{card.value}</p>
            <p className="text-xs text-muted-foreground mt-1.5">{card.sub}</p>
          </div>
        );
      })}
    </div>
  );
}